import React from "react";

interface CompanyIconProps {
  company: string;
  theme: "light" | "dark";
}

export const CompanyIcon: React.FC<CompanyIconProps> = ({ company, theme }) => {
  const isDark = theme === "dark";
  const primaryColor = isDark ? "#6B9FFF" : "#0066FF";
  const secondaryColor = isDark ? "#4A90E2" : "#4D9FFF";
  const accentColor = isDark ? "#FF6B9D" : "#FF1B6D";
  const bgColor = isDark ? "#1A1A1A" : "#FFFFFF";
  const borderColor = "#000000";

  const getIcon = () => {
    switch (company) {
      case "MindX Technology School":
        // Graduation cap with book
        return (
          <svg
            viewBox="0 0 200 200"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ width: "100%", height: "100%" }}
          >
            {/* Book base */}
            <rect
              x="50"
              y="120"
              width="100"
              height="60"
              fill={secondaryColor}
              stroke={borderColor}
              strokeWidth="4"
            />
            <rect
              x="50"
              y="120"
              width="100"
              height="15"
              fill={primaryColor}
              stroke={borderColor}
              strokeWidth="4"
            />
            <line
              x1="100"
              y1="120"
              x2="100"
              y2="180"
              stroke={borderColor}
              strokeWidth="3"
            />

            {/* Graduation cap */}
            <polygon
              points="100,40 170,70 100,100 30,70"
              fill={primaryColor}
              stroke={borderColor}
              strokeWidth="4"
            />
            <rect
              x="95"
              y="100"
              width="10"
              height="30"
              fill={accentColor}
              stroke={borderColor}
              strokeWidth="4"
            />
            <circle
              cx="100"
              cy="130"
              r="8"
              fill={accentColor}
              stroke={borderColor}
              strokeWidth="3"
            />

            {/* Tassel */}
            <line
              x1="100"
              y1="138"
              x2="100"
              y2="155"
              stroke={borderColor}
              strokeWidth="3"
            />
            <polygon
              points="95,155 100,165 105,155"
              fill={accentColor}
              stroke={borderColor}
              strokeWidth="2"
            />
          </svg>
        );

      case "SotaNext JSC":
        // NFT cube with blockchain
        return (
          <svg
            viewBox="0 0 200 200"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ width: "100%", height: "100%" }}
          >
            {/* 3D Cube */}
            <polygon
              points="100,50 160,80 160,140 100,170"
              fill={primaryColor}
              stroke={borderColor}
              strokeWidth="4"
            />
            <polygon
              points="100,50 40,80 40,140 100,170"
              fill={secondaryColor}
              stroke={borderColor}
              strokeWidth="4"
            />
            <polygon
              points="40,80 100,50 160,80 100,110"
              fill={accentColor}
              stroke={borderColor}
              strokeWidth="4"
            />

            {/* NFT symbol inside */}
            <text
              x="100"
              y="120"
              fontSize="28"
              fontWeight="900"
              fill={bgColor}
              textAnchor="middle"
              fontFamily="monospace"
            >
              NFT
            </text>

            {/* Blockchain nodes */}
            <circle
              cx="30"
              cy="50"
              r="8"
              fill={accentColor}
              stroke={borderColor}
              strokeWidth="3"
            />
            <circle
              cx="170"
              cy="50"
              r="8"
              fill={accentColor}
              stroke={borderColor}
              strokeWidth="3"
            />
            <circle
              cx="30"
              cy="170"
              r="8"
              fill={accentColor}
              stroke={borderColor}
              strokeWidth="3"
            />
            <circle
              cx="170"
              cy="170"
              r="8"
              fill={accentColor}
              stroke={borderColor}
              strokeWidth="3"
            />

            {/* Connection lines */}
            <line
              x1="30"
              y1="50"
              x2="40"
              y2="80"
              stroke={borderColor}
              strokeWidth="2"
              strokeDasharray="4 4"
            />
            <line
              x1="170"
              y1="50"
              x2="160"
              y2="80"
              stroke={borderColor}
              strokeWidth="2"
              strokeDasharray="4 4"
            />
          </svg>
        );

      case "Phong Linh Tech":
        // Code brackets with lightbulb
        return (
          <svg
            viewBox="0 0 200 200"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ width: "100%", height: "100%" }}
          >
            {/* Left bracket */}
            <path
              d="M 60 50 L 40 50 L 40 150 L 60 150"
              stroke={borderColor}
              strokeWidth="6"
              fill="none"
              strokeLinecap="square"
            />
            <path
              d="M 55 50 L 35 50 L 35 150 L 55 150"
              stroke={primaryColor}
              strokeWidth="4"
              fill="none"
              strokeLinecap="square"
            />

            {/* Right bracket */}
            <path
              d="M 140 50 L 160 50 L 160 150 L 140 150"
              stroke={borderColor}
              strokeWidth="6"
              fill="none"
              strokeLinecap="square"
            />
            <path
              d="M 145 50 L 165 50 L 165 150 L 145 150"
              stroke={primaryColor}
              strokeWidth="4"
              fill="none"
              strokeLinecap="square"
            />

            {/* Lightbulb */}
            <circle
              cx="100"
              cy="80"
              r="25"
              fill={secondaryColor}
              stroke={borderColor}
              strokeWidth="4"
            />
            <path
              d="M 85 100 Q 85 110 90 115 L 110 115 Q 115 110 115 100"
              fill={primaryColor}
              stroke={borderColor}
              strokeWidth="4"
            />
            <rect
              x="92"
              y="115"
              width="16"
              height="10"
              fill={primaryColor}
              stroke={borderColor}
              strokeWidth="3"
            />
            <rect
              x="95"
              y="125"
              width="10"
              height="6"
              fill={accentColor}
              stroke={borderColor}
              strokeWidth="3"
            />

            {/* Light rays */}
            <line
              x1="100"
              y1="50"
              x2="100"
              y2="40"
              stroke={accentColor}
              strokeWidth="4"
              strokeLinecap="round"
            />
            <line
              x1="130"
              y1="65"
              x2="138"
              y2="57"
              stroke={accentColor}
              strokeWidth="4"
              strokeLinecap="round"
            />
            <line
              x1="70"
              y1="65"
              x2="62"
              y2="57"
              stroke={accentColor}
              strokeWidth="4"
              strokeLinecap="round"
            />
          </svg>
        );

      case "Revotech":
        // Gear/cog with tools
        return (
          <svg
            viewBox="0 0 200 200"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ width: "100%", height: "100%" }}
          >
            {/* Main gear */}
            <circle
              cx="100"
              cy="100"
              r="40"
              fill={secondaryColor}
              stroke={borderColor}
              strokeWidth="4"
            />

            {/* Gear teeth */}
            {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => {
              const rad = (angle * Math.PI) / 180;
              const x2 = 100 + Math.cos(rad) * 55;
              const y2 = 100 + Math.sin(rad) * 55;
              return (
                <rect
                  key={i}
                  x={x2 - 8}
                  y={y2 - 6}
                  width="16"
                  height="12"
                  fill={primaryColor}
                  stroke={borderColor}
                  strokeWidth="3"
                  transform={`rotate(${angle}, ${x2}, ${y2})`}
                />
              );
            })}

            {/* Center circle */}
            <circle
              cx="100"
              cy="100"
              r="20"
              fill={bgColor}
              stroke={borderColor}
              strokeWidth="4"
            />
            <circle
              cx="100"
              cy="100"
              r="10"
              fill={accentColor}
              stroke={borderColor}
              strokeWidth="3"
            />

            {/* Wrench */}
            <rect
              x="145"
              y="40"
              width="8"
              height="40"
              fill={accentColor}
              stroke={borderColor}
              strokeWidth="3"
              transform="rotate(45, 149, 60)"
            />
            <rect
              x="142"
              y="35"
              width="14"
              height="14"
              fill={accentColor}
              stroke={borderColor}
              strokeWidth="3"
              transform="rotate(45, 149, 42)"
            />

            {/* Screwdriver */}
            <rect
              x="40"
              y="145"
              width="6"
              height="35"
              fill={primaryColor}
              stroke={borderColor}
              strokeWidth="3"
              transform="rotate(-45, 43, 162)"
            />
            <polygon
              points="40,145 46,145 43,135"
              fill={accentColor}
              stroke={borderColor}
              strokeWidth="3"
              transform="rotate(-45, 43, 140)"
            />
          </svg>
        );

      default:
        // Default building icon
        return (
          <svg
            viewBox="0 0 200 200"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ width: "100%", height: "100%" }}
          >
            <rect
              x="50"
              y="60"
              width="100"
              height="120"
              fill={primaryColor}
              stroke={borderColor}
              strokeWidth="4"
            />
            <rect
              x="70"
              y="80"
              width="20"
              height="20"
              fill={bgColor}
              stroke={borderColor}
              strokeWidth="3"
            />
            <rect
              x="110"
              y="80"
              width="20"
              height="20"
              fill={bgColor}
              stroke={borderColor}
              strokeWidth="3"
            />
            <rect
              x="70"
              y="120"
              width="20"
              height="20"
              fill={bgColor}
              stroke={borderColor}
              strokeWidth="3"
            />
            <rect
              x="110"
              y="120"
              width="20"
              height="20"
              fill={bgColor}
              stroke={borderColor}
              strokeWidth="3"
            />
            <rect
              x="85"
              y="150"
              width="30"
              height="30"
              fill={accentColor}
              stroke={borderColor}
              strokeWidth="3"
            />
          </svg>
        );
    }
  };

  return <div style={{ width: "100%", height: "100%" }}>{getIcon()}</div>;
};
